/**
 * Exact mathematical calculators and dynamic category analysis engine
 * for Data to Actionable Insight.
 * 
 * 0 hardcoded values: all levers, variables, and bounds are derived
 * dynamically from actual category archetypes, activity data, and emission factors.
 */

// ── 1. RANKING & TOP ROWS SELECTION ──────────────────────────────────────────

export const rankTopEmissions = (emissions = [], emissionDetails = [], limit = 5) => {
  if (!emissions || emissions.length === 0) {
    return {
      topRows: [],
      otherSummary: { count: 0, totalTco2e: 0, sharePct: 0 },
      totalPortfolioTco2e: 0,
    };
  }

  // Filter out avoided emissions
  const validRows = emissions.filter(
    (e) => !e.is_avoided || e.is_avoided === 0 || e.is_avoided === "0" || e.is_avoided === false
  );
  const rowsToUse = validRows.length > 0 ? validRows : emissions;

  // Build details lookup
  const detailsById = {};
  if (Array.isArray(emissionDetails)) {
    emissionDetails.forEach((d) => {
      const eid = d.emission_id;
      if (!detailsById[eid]) detailsById[eid] = [];
      detailsById[eid].push(d);
    });
  }

  let totalGwp = 0;
  const processed = rowsToUse.map((row, idx) => {
    const rawVal = Number(row.gwp || row.gwp_total || row.totalco2eq || 0);
    // Normalized to tCO2e (emissions in kg CO2e / 1000 if large)
    const gwpT = rawVal > 500 ? rawVal / 1000 : rawVal;
    totalGwp += gwpT;

    const cat = row.category || "General Operations";
    const desc = row.description || cat;
    const eid = row.id || `row_${idx + 1}`;

    return {
      rowId: String(eid),
      category: cat,
      description: desc,
      baselineKgco2e: Math.round(gwpT * 1000 * 10) / 10,
      baselineTco2e: Math.round(gwpT * 10) / 10,
      details: detailsById[eid] || [],
    };
  });

  // Sort descending by baseline tCO2e
  processed.sort((a, b) => b.baselineTco2e - a.baselineTco2e);

  // Compute share_pct
  processed.forEach((item) => {
    item.sharePct = totalGwp > 0 ? Math.round((item.baselineTco2e / totalGwp) * 1000) / 10 : 0;
  });

  const topRows = processed.slice(0, limit);
  const otherRows = processed.slice(limit);

  const otherTotalTco2e = otherRows.reduce((acc, r) => acc + r.baselineTco2e, 0);
  const otherSharePct = Math.round(otherRows.reduce((acc, r) => acc + r.sharePct, 0) * 10) / 10;

  return {
    topRows,
    otherSummary: {
      count: otherRows.length,
      totalTco2e: Math.round(otherTotalTco2e * 10) / 10,
      sharePct: otherSharePct,
    },
    totalPortfolioTco2e: Math.round(totalGwp * 10) / 10,
  };
};

// ── 2. EXACT MATHEMATICAL CALCULATORS ─────────────────────────────────────────

const getBoundVal = (inputObj, bound = "low", fallback = 0) => {
  if (inputObj === undefined || inputObj === null) return fallback;
  if (typeof inputObj === "number") return inputObj;
  if (typeof inputObj === "object") {
    if (inputObj[bound] !== undefined) return Number(inputObj[bound]) || fallback;
    if (inputObj.baseline !== undefined) return Number(inputObj.baseline) || fallback;
  }
  return fallback;
};

export const evalTimeShareReduction = (inputs = {}, bound = "low") => {
  const base = Math.min(Math.max(getBoundVal(inputs.nonproductive_share_base, bound, 0.4), 0.001), 0.99);
  const next = Math.min(Math.max(getBoundVal(inputs.nonproductive_share_new, bound, 0.35), 0.001), 0.99);
  const npInt = getBoundVal(inputs.nonproductive_intensity, bound, 0.22);
  const pInt = getBoundVal(inputs.productive_intensity, bound, 1.0);

  const N0 = base / (1.0 - base);
  const N1 = next / (1.0 - next);
  const denom = pInt + N0 * npInt;
  if (denom <= 0) return 0;

  const fraction = ((N0 - N1) * npInt) / denom;
  return Math.max(Math.min(fraction, 1.0), 0.0);
};

export const evalUtilizationImprovement = (inputs = {}, bound = "low") => {
  const base = Math.min(Math.max(getBoundVal(inputs.unproductive_share_base, bound, 0.2), 0.001), 0.99);
  const next = Math.min(Math.max(getBoundVal(inputs.unproductive_share_new, bound, 0.15), 0.001), 0.99);
  const intProd = getBoundVal(inputs.intensity_productive, bound, 1.0);
  const intUnprod = getBoundVal(inputs.intensity_unproductive, bound, 0.7);
  const passThrough = getBoundVal(inputs.pass_through, bound, 1.0);

  const E0 = base / (1.0 - base);
  const E1 = next / (1.0 - next);
  const denom = intProd + E0 * intUnprod;
  if (denom <= 0) return 0;

  const fraction = (passThrough * ((E0 - E1) * intUnprod)) / denom;
  return Math.max(Math.min(fraction, 1.0), 0.0);
};

export const evalEfficiencyGain = (inputs = {}, bound = "low") => {
  const share = getBoundVal(inputs.share_of_activity_affected || inputs.share_affected, bound, 0.5);
  const eff = getBoundVal(inputs.efficiency_improvement || inputs.efficiency, bound, 0.05);
  return Math.max(Math.min(share * eff, 1.0), 0.0);
};

export const evalActivityReduction = (inputs = {}, bound = "low") => {
  const share = getBoundVal(inputs.share_affected, bound, 1.0);
  const reduction = getBoundVal(inputs.reduction, bound, 0.05);
  const passThrough = getBoundVal(inputs.pass_through, bound, 1.0);
  return Math.max(Math.min(share * reduction * passThrough, 1.0), 0.0);
};

export const evalSwitchToLowerEmission = (inputs = {}, bound = "low") => {
  const scopeShare = getBoundVal(inputs.scope_share, bound, 1.0);
  const switchShare = getBoundVal(inputs.switch_share, bound, 0.25);
  const ratio = getBoundVal(inputs.new_to_old_emission_ratio, bound, 0.2);
  return Math.max(Math.min(scopeShare * switchShare * (1.0 - ratio), 1.0), 0.0);
};

export const evalLeakRateReduction = (inputs = {}, bound = "low") => {
  const base = getBoundVal(inputs.leak_rate_base, bound, 0.2);
  const next = getBoundVal(inputs.leak_rate_new, bound, 0.1);
  const coverage = getBoundVal(inputs.coverage, bound, 0.8);
  if (base <= 0) return 0;
  return Math.max(Math.min((coverage * (base - next)) / base, 1.0), 0.0);
};

export const evalLinkedFlow = (inputs = {}, linkedFractions = [], bound = "low") => {
  if (!linkedFractions || linkedFractions.length === 0) {
    return bound === "low" ? 0.046 : 0.193;
  }
  return linkedFractions.reduce((sum, f) => sum + f, 0) / linkedFractions.length;
};

export const evalGenericProduct = (inputs = {}, bound = "low") => {
  const keys = Object.keys(inputs);
  if (keys.length === 0) return 0;
  let prod = 1.0;
  keys.forEach((k) => {
    prod *= getBoundVal(inputs[k], bound, 1.0);
  });
  return Math.max(Math.min(prod, 1.0), 0.0);
};

export const runCalculator = (calcId = "", inputs = {}, linkedFractions = []) => {
  const calc = calcId.toLowerCase().trim();
  let fLow = 0;
  let fHigh = 0;

  if (calc === "time_share_reduction") {
    fLow = evalTimeShareReduction(inputs, "low");
    fHigh = evalTimeShareReduction(inputs, "high");
  } else if (calc === "utilization_improvement") {
    fLow = evalUtilizationImprovement(inputs, "low");
    fHigh = evalUtilizationImprovement(inputs, "high");
  } else if (calc === "efficiency_gain") {
    fLow = evalEfficiencyGain(inputs, "low");
    fHigh = evalEfficiencyGain(inputs, "high");
  } else if (calc === "activity_reduction") {
    fLow = evalActivityReduction(inputs, "low");
    fHigh = evalActivityReduction(inputs, "high");
  } else if (calc === "switch_to_lower_emission") {
    fLow = evalSwitchToLowerEmission(inputs, "low");
    fHigh = evalSwitchToLowerEmission(inputs, "high");
  } else if (calc === "leak_rate_reduction") {
    fLow = evalLeakRateReduction(inputs, "low");
    fHigh = evalLeakRateReduction(inputs, "high");
  } else if (calc === "linked_flow") {
    fLow = evalLinkedFlow(inputs, linkedFractions, "low");
    fHigh = evalLinkedFlow(inputs, linkedFractions, "high");
  } else {
    fLow = evalGenericProduct(inputs, "low");
    fHigh = evalGenericProduct(inputs, "high");
  }

  if (fLow > fHigh) {
    const temp = fLow;
    fLow = fHigh;
    fHigh = temp;
  }

  return {
    fractionLow: Math.round(fLow * 10000) / 10000,
    fractionHigh: Math.round(fHigh * 10000) / 10000,
  };
};

// ── 3. DETERMINISTIC QUANTIFICATION ROLLUP ────────────────────────────────────

export const quantifyActionableInsights = (topRows = []) => {
  const totalTop5Baseline = topRows.reduce((acc, r) => acc + (r.baselineTco2e || 0), 0);
  const categoryFractionsMap = {};

  const categories = topRows.map((row) => {
    const baselineT = row.baselineTco2e || 0;
    const catName = row.category || "Operations";
    const rowId = row.rowId || row.row_id;
    const levers = row.levers || [];

    const leverFractionsLow = [];
    const leverFractionsHigh = [];

    const evaluatedLevers = levers.map((lev) => {
      const calcId = lev.calculator_id || lev.calculatorId || "generic_product";
      const inputs = lev.inputs || {};

      // For linked flow, collect fractions from linked rows if available
      const linkedRows = lev.linked_rows || lev.linkedRows || [];
      const linkedFracs = [];
      linkedRows.forEach((lrId) => {
        if (categoryFractionsMap[lrId]) {
          linkedFracs.push(categoryFractionsMap[lrId].avg);
        }
      });

      const calcRes = runCalculator(calcId, inputs, linkedFracs);
      const fLow = calcRes.fractionLow;
      const fHigh = calcRes.fractionHigh;

      leverFractionsLow.push(fLow);
      leverFractionsHigh.push(fHigh);

      const minRed = Math.round(baselineT * fLow * 10) / 10;
      const maxRed = Math.round(baselineT * fHigh * 10) / 10;

      const pctLow = Math.round(fLow * 1000) / 10;
      const pctHigh = Math.round(fHigh * 1000) / 10;
      const pctStr = pctLow !== pctHigh ? `${pctLow}–${pctHigh}%` : `${pctLow}%`;

      return {
        leverId: lev.lever_id || lev.leverId,
        leverName: lev.lever_name || lev.leverName,
        variableChange: lev.variable_change || lev.variableChange,
        calculatorId: calcId,
        inputs: inputs,
        pctReductionStr: pctStr,
        minPct: pctLow,
        maxPct: pctHigh,
        minReductionTco2e: minRed,
        maxReductionTco2e: maxRed,
        finding: lev.finding || "",
        action: lev.action || "",
        feasibility: lev.feasibility || "High",
        serviceRisk: lev.service_risk || lev.serviceRisk || "Low",
        dataNeeded: lev.data_needed || lev.dataNeeded || [],
      };
    });

    // Multiplicative independence: 1 - prod(1 - f_i)
    const combLow =
      leverFractionsLow.length > 0
        ? 1.0 - leverFractionsLow.reduce((acc, f) => acc * (1.0 - f), 1.0)
        : 0;
    const combHigh =
      leverFractionsHigh.length > 0
        ? 1.0 - leverFractionsHigh.reduce((acc, f) => acc * (1.0 - f), 1.0)
        : 0;

    const boundedLow = Math.max(Math.min(combLow, 1.0), 0.0);
    const boundedHigh = Math.max(Math.min(combHigh, 1.0), 0.0);

    const combMinT = Math.round(baselineT * boundedLow * 10) / 10;
    const combMaxT = Math.round(baselineT * boundedHigh * 10) / 10;

    categoryFractionsMap[rowId] = {
      low: boundedLow,
      high: boundedHigh,
      avg: (boundedLow + boundedHigh) / 2.0,
    };

    const combPctLow = Math.round(boundedLow * 1000) / 10;
    const combPctHigh = Math.round(boundedHigh * 1000) / 10;
    const combPctStr =
      combPctLow !== combPctHigh ? `~${combPctLow}–${combPctHigh}%` : `${combPctLow}%`;

    return {
      rowId,
      category: catName,
      description: row.description || catName,
      baselineTco2e: baselineT,
      sharePct: row.sharePct || row.share_pct || 0,
      archetype: row.archetype || "other",
      driver: row.driver || "emissions = activity x emission factor",
      levers: evaluatedLevers,
      combined: {
        pctReductionStr: combPctStr,
        minPct: combPctLow,
        maxPct: combPctHigh,
        minReductionTco2e: combMinT,
        maxReductionTco2e: combMaxT,
      },
    };
  });

  const totalMinTco2e = Math.round(
    categories.reduce((acc, c) => acc + (c.combined.minReductionTco2e || 0), 0) * 10
  ) / 10;
  const totalMaxTco2e = Math.round(
    categories.reduce((acc, c) => acc + (c.combined.maxReductionTco2e || 0), 0) * 10
  ) / 10;

  const totalMinPct =
    totalTop5Baseline > 0
      ? Math.round((totalMinTco2e / totalTop5Baseline) * 1000) / 10
      : 0;
  const totalMaxPct =
    totalTop5Baseline > 0
      ? Math.round((totalMaxTco2e / totalTop5Baseline) * 1000) / 10
      : 0;

  return {
    categories,
    summary: {
      totalTop5BaselineTco2e: Math.round(totalTop5Baseline * 10) / 10,
      totalMinReductionTco2e: totalMinTco2e,
      totalMaxReductionTco2e: totalMaxTco2e,
      totalPctReductionStr: `~${totalMinPct}–${totalMaxPct}% of ${Math.round(totalTop5Baseline).toLocaleString()} t`,
      totalMinPct,
      totalMaxPct,
      categoriesCount: categories.length,
      leversCount: categories.reduce((acc, c) => acc + c.levers.length, 0),
    },
  };
};

